import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Tabs, Tab,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, TextField, TablePagination,
  InputAdornment, MenuItem,
} from '@mui/material';
import { Add, CheckCircle, Cancel, Search } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentRequestService, projectService, farmOperationService } from '@smartgrow/shared';
import type { PaymentRequest, PaymentRequestStatus } from '@smartgrow/shared/types';

const statusColor = (status: PaymentRequestStatus) => {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'REJECTED': return 'error';
    case 'PAID': return 'info';
    default: return 'warning';
  }
};

export const PaymentRequestsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId') ?? '';
  const projectName = searchParams.get('projectName') ?? '';
  const [tab, setTab] = useState<'ALL' | PaymentRequestStatus>('ALL');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId);
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');

  const projectId = selectedProjectId || urlProjectId;

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdmin = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', projectId],
    queryFn: () => farmOperationService.getByProject(projectId),
    enabled: !!projectId,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['tasks-for-filter', selectedOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(selectedOperationId),
    enabled: !!selectedOperationId,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['payment-requests', tab, isAdmin, page, projectId, selectedOperationId, selectedTaskId, debouncedSearch],
    queryFn: () => {
      if (!isAdmin) return paymentRequestService.getMyRequests({ page, size: 10 });
      return paymentRequestService.search({
        page, size: 10,
        search: debouncedSearch || undefined,
        status: tab === 'ALL' ? undefined : tab,
        projectId: projectId || undefined,
        farmOperationId: selectedOperationId || undefined,
        operationTaskId: selectedTaskId || undefined,
      });
    },
  });

  const clearProjectFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('projectName');
    setSearchParams(next);
    setSelectedProjectId('');
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
  };

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
    if (!value && urlProjectId) clearProjectFilter();
  };

  const handleOperationChange = (value: string) => {
    setSelectedOperationId(value);
    setSelectedTaskId('');
    setPage(0);
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => paymentRequestService.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-requests'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      paymentRequestService.reject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
    },
  });

  const rows: PaymentRequest[] = data?.content ?? (Array.isArray(data) ? (data as PaymentRequest[]) : []);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Payment Requests</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/payments/requests/new')}>
          New Request
        </Button>
      </Box>

      {urlProjectId && !selectedProjectId && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Project: ${projectName || urlProjectId}`}
            onDelete={clearProjectFilter}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Project" value={projectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Operation" value={selectedOperationId}
            onChange={(e) => handleOperationChange(e.target.value)} disabled={!projectId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Operations</MenuItem>
            {(operationsData?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Task" value={selectedTaskId}
            onChange={(e) => { setSelectedTaskId(e.target.value); setPage(0); }} disabled={!selectedOperationId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Tasks</MenuItem>
            {(tasksData ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField
          fullWidth
          placeholder="Search by request #, description, contractor, or requester..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {isAdmin && (
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} variant="scrollable" scrollButtons="auto">
            <Tab label="All" value="ALL" />
            <Tab label="Pending" value="PENDING" />
            <Tab label="Approved" value="APPROVED" />
            <Tab label="Rejected" value="REJECTED" />
            <Tab label="Paid" value="PAID" />
          </Tabs>
        </Paper>
      )}

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load payment requests.</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Request #</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((req, i) => (
                <TableRow key={req.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/payments/requests/${req.id}`)}
                >
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{req.requestNumber}</Typography></TableCell>
                  <TableCell><Chip label={req.requestType} variant="outlined" size="small" /></TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap title={req.description}>{req.description}</Typography>
                    {req.contractorName && (
                      <Typography variant="caption" color="text.secondary">{req.contractorName}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">₦{req.amount.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>{req.requestedByName}</TableCell>
                  <TableCell>{new Date(req.requestDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={req.status} color={statusColor(req.status) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    {isAdmin && req.status === 'PENDING' && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={(e) => { e.stopPropagation(); approveMutation.mutate(req.id); }}
                          disabled={approveMutation.isPending}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={(e) => { e.stopPropagation(); setRejectDialog({ open: true, id: req.id }); }}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No payment requests found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalElements ?? 0}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null })} fullWidth maxWidth="sm">
        <DialogTitle>Reject Payment Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Provide a reason for rejection.</DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, id: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() => rejectDialog.id && rejectMutation.mutate({ id: rejectDialog.id, reason: rejectReason })}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
